import { useState } from "react"
import { PropTypes } from "prop-types"

import Translate from "../Translate"

import PaginationPage from "./PaginationPage"
import { getNumberedPages } from "./utils"

/**
 * Component that handles pagination
 * @param       {number}   numberOfPages   Number of pages in total
 * @param       {number}   maxPages        Max number of pages that will be displayed at once, the others will be accessed using the arrows
 * @param       {function} onChange        Update active page
 * @returns
 */
export function Pagination(props) {
  const { ariaLabel, onChange, defaultActivePage = 1, maxPages = 5, numberOfPages = 1 } = props
  const [activePage, setActivePage] = useState(defaultActivePage)

  const handleChange = pageNumber => {
    setActivePage(pageNumber)

    if (onChange) {
      onChange(pageNumber)
    }
  }

  const isFirstPageActive = activePage === 1
  const isLastPageActive = activePage === numberOfPages
  const numberedPages = getNumberedPages(numberOfPages, maxPages, activePage)

  return (
    <Translate>
      {({ translate }) => (
        <nav className="oj-pagination" aria-label={ariaLabel}>
          <ul>
            <PaginationPage
              ariaLabel={translate("accessPaginationFirstPage")}
              type="angle-double-left"
              disabled={isFirstPageActive}
              value={1}
              onChange={handleChange}
            />
            <PaginationPage
              ariaLabel={translate("accessPaginationPrevPage")}
              type="angle-left"
              disabled={isFirstPageActive}
              value={activePage - 1}
              onChange={handleChange}
            />
            {numberedPages.map(currentPage => {
              const isActive = currentPage === activePage

              return (
                <PaginationPage
                  key={`page-${currentPage}`}
                  active={isActive}
                  disabled={isActive}
                  ariaLabel={translate("accessPaginationPage", { pageNumber: currentPage })}
                  type="number"
                  label={currentPage}
                  value={currentPage}
                  onChange={handleChange}
                />
              )
            })}
            <PaginationPage
              ariaLabel={translate("accessPaginationNextPage")}
              type="angle-right"
              disabled={isLastPageActive}
              value={activePage + 1}
              onChange={handleChange}
            />
            <PaginationPage
              ariaLabel={translate("accessPaginationLastPage")}
              type="angle-double-right"
              disabled={isLastPageActive}
              value={numberOfPages}
              onChange={handleChange}
            />
          </ul>
        </nav>
      )}
    </Translate>
  )
}

Pagination.propTypes = {
  ariaLabel: PropTypes.string,
  defaultActivePage: PropTypes.number,
  maxPages: PropTypes.number,
  numberOfPages: PropTypes.number,
  onChange: PropTypes.func
}

export default Pagination
